import {
  createSocialStory,
  deleteSocialStory,
  fetchSocialStoriesWire,
  normalizeStoryUserId,
  parseSocialStoriesAsUserGroups,
  parseSocialStoriesList,
} from '../api/socialStoriesApi';

export type SocialPostMediaKind = 'imageAudio' | 'video';

export type SocialPostRecord = {
  id: string;
  userId: string;
  userLabel: string;
  userAvatarSrc: string | null;
  text: string;
  mediaKind: SocialPostMediaKind;
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
  createdAt: number;
  expiresAt: number;
};

export type SocialPostUserGroup = {
  userId: string;
  userLabel: string;
  userAvatarSrc: string | null;
  posts: SocialPostRecord[];
};

/** Skip persisting huge inline avatars that can blow request size limits. */
/** API validation: `userAvatarSrc must be shorter than or equal to 2048 characters`. */
export const SOCIAL_STORY_AVATAR_SRC_MAX_CHARS = 2048;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasMediaUrl(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function isStoryMediaPath(src: string): boolean {
  return /\/stories\/[^/?#]+\/[^/?#]+/i.test(src) && !/\/profiles\//i.test(src);
}

function isBrowserProxyAvatarPath(src: string): boolean {
  return src.startsWith('/dev-api-proxy') || src.startsWith('/dev-login-proxy');
}

export function isSocialPostRecord(value: unknown): value is SocialPostRecord {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.id === 'string' &&
    typeof value.userId === 'string' &&
    typeof value.userLabel === 'string' &&
    (value.userAvatarSrc === null || typeof value.userAvatarSrc === 'string') &&
    typeof value.text === 'string' &&
    (value.mediaKind === 'imageAudio' || value.mediaKind === 'video') &&
    typeof value.createdAt === 'number' &&
    typeof value.expiresAt === 'number'
  );
}

/** True when the post has enough media to show in the story viewer. */
export function socialPostHasPlayableMedia(post: SocialPostRecord): boolean {
  if (post.mediaKind === 'video') {
    return hasMediaUrl(post.videoUrl);
  }
  return hasMediaUrl(post.imageUrl);
}

export function sanitizeSocialPostAvatarSrc(avatarSrc: string | null | undefined): string | null {
  if (typeof avatarSrc !== 'string') {
    return null;
  }
  const trimmed = avatarSrc.trim();
  if (!trimmed || isStoryMediaPath(trimmed)) {
    return null;
  }
  if (trimmed.length > SOCIAL_STORY_AVATAR_SRC_MAX_CHARS) {
    return null;
  }
  return trimmed;
}

/** Persist a shareable avatar for stories — remote URL under the API 2048-char cap. */
export function storyAvatarSrcForStorage(avatarSrc: string | null | undefined): string | null {
  return sanitizeSocialPostAvatarSrc(avatarSrc);
}

async function resolveRemoteProfileAvatarFromServer(): Promise<string | null> {
  try {
    const { fetchProfile } = await import('../api/profileApi');
    const { profileImageFromRecord } = await import('./profileDisplaySummary');
    const data = await fetchProfile();
    const candidates: unknown[] = [data];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const root = data as Record<string, unknown>;
      for (const key of ['data', 'user', 'profile', 'client', 'result', 'payload'] as const) {
        candidates.push(root[key]);
      }
    }
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        continue;
      }
      const raw = profileImageFromRecord(candidate as Record<string, unknown>);
      const stored = storyAvatarSrcForStorage(raw);
      if (!stored || isBrowserProxyAvatarPath(stored) || isStoryMediaPath(stored)) {
        continue;
      }
      if (/^https?:\/\//i.test(stored) || stored.startsWith('/')) {
        return stored;
      }
    }
  } catch {
    /* profile optional for publish */
  }
  return null;
}

/**
 * Resolve a chip avatar the stories API will accept (≤2048 chars).
 * Prefer remote HTTPS profile URLs; never send oversized data URLs (400 from API).
 *
 * Important: a local `data:` crop must not fall back to a stale GET /profile URL —
 * that is how Profile can show a new face while story chips keep an old photo.
 */
export async function resolveStoryAvatarForPublish(
  displayAvatarSrc: string | null | undefined,
): Promise<string | null> {
  const trimmed =
    typeof displayAvatarSrc === 'string' && displayAvatarSrc.trim() ? displayAvatarSrc.trim() : null;

  // Google / other remote HTTPS already in the live display path.
  if (trimmed && /^https?:\/\//i.test(trimmed) && !isStoryMediaPath(trimmed)) {
    const stored = storyAvatarSrcForStorage(trimmed);
    if (stored) {
      return stored;
    }
  }

  // Local crop: sync to the profile API first so other users see the same face.
  if (trimmed?.startsWith('data:image/')) {
    try {
      const { uploadProfileAvatarToServer } = await import('./profileAvatarUpload');
      const { saveLocalProfileAvatarSrc } = await import('./profileAvatarStorage');
      const remote = await uploadProfileAvatarToServer(trimmed);
      if (remote) {
        const stored = storyAvatarSrcForStorage(remote);
        if (stored) {
          saveLocalProfileAvatarSrc(stored);
          return stored;
        }
      }
    } catch {
      /* fall through to tiny data-URL / omit — never use a stale server face */
    }

    try {
      const { compressProfileAvatarDataUrlForUpload } = await import('./profileAvatarUpload');
      // ~1.2KB decoded ≈ data-URL under 2048 chars.
      const compressed = await compressProfileAvatarDataUrlForUpload(trimmed, 1_200);
      const stored = storyAvatarSrcForStorage(compressed);
      if (stored) {
        return stored;
      }
    } catch {
      /* ignore compress failures */
    }

    // Better initials than publishing an outdated GET /profile photo.
    return null;
  }

  return resolveRemoteProfileAvatarFromServer();
}

export function purgeExpiredSocialPosts(records: SocialPostRecord[], now = Date.now()): SocialPostRecord[] {
  return records.filter((post) => post.expiresAt > now);
}

export function groupSocialPostsByUser(records: SocialPostRecord[]): SocialPostUserGroup[] {
  const map = new Map<string, SocialPostUserGroup>();
  for (const post of records) {
    if (!socialPostHasPlayableMedia(post)) {
      continue;
    }
    const userId = normalizeStoryUserId(post.userId);
    const existing = map.get(userId);
    if (existing) {
      existing.posts.push(post);
      if (post.userAvatarSrc && !existing.userAvatarSrc) {
        existing.userAvatarSrc = post.userAvatarSrc;
      }
      continue;
    }
    map.set(userId, {
      userId,
      userLabel: post.userLabel,
      userAvatarSrc: post.userAvatarSrc,
      posts: [{ ...post, userId }],
    });
  }
  const groups = Array.from(map.values());
  for (const group of groups) {
    group.posts.sort((a, b) => a.createdAt - b.createdAt);
  }
  groups.sort((a, b) => {
    const aLatest = a.posts[a.posts.length - 1]?.createdAt ?? 0;
    const bLatest = b.posts[b.posts.length - 1]?.createdAt ?? 0;
    return bLatest - aLatest;
  });
  return groups;
}

/** GET `/stories/` — active story groups for the home strip. */
export async function listActiveSocialPostGroups(now = Date.now()): Promise<SocialPostUserGroup[]> {
  try {
    const raw = await fetchSocialStoriesWire();
    const grouped = parseSocialStoriesAsUserGroups(raw);
    const baseGroups =
      grouped ??
      groupSocialPostsByUser(
        purgeExpiredSocialPosts(parseSocialStoriesList(raw), now),
      );
    return baseGroups
      .map((group) => ({
        ...group,
        posts: purgeExpiredSocialPosts(group.posts, now),
      }))
      .filter((group) => group.posts.length > 0);
  } catch (err) {
    console.warn('[socialPosts] Failed to load stories', err);
    return [];
  }
}

/** GET `/stories/` — flat active stories from the API. */
export async function listActiveSocialPosts(now = Date.now()): Promise<SocialPostRecord[]> {
  const groups = await listActiveSocialPostGroups(now);
  return groups.flatMap((group) => group.posts).sort((a, b) => b.createdAt - a.createdAt);
}

/** Delete a single story by id (used by the owner remove action). */
export async function deleteSocialPost(postId: string): Promise<void> {
  await deleteSocialStory(postId);
}

export type AddSocialPostInput = {
  userId: string;
  userLabel: string;
  userAvatarSrc: string | null;
  text: string;
  mediaKind: SocialPostMediaKind;
  imageFile?: Blob;
  audioFile?: Blob;
  videoFile?: Blob;
};

export class SocialPostLimitError extends Error {
  constructor() {
    super('social-post-limit');
    this.name = 'SocialPostLimitError';
  }
}

function isUsableBlob(value: unknown): value is Blob {
  return (
    typeof Blob !== 'undefined' &&
    value instanceof Blob &&
    Number.isFinite(value.size) &&
    value.size > 0
  );
}

/** POST `/stories` — multipart upload. */
export async function addSocialPost(input: AddSocialPostInput): Promise<SocialPostRecord> {
  if (input.mediaKind === 'video') {
    if (!isUsableBlob(input.videoFile)) {
      throw new Error('social-missing-media');
    }
  } else if (!isUsableBlob(input.imageFile)) {
    throw new Error('social-missing-media');
  }

  try {
    return await createSocialStory({
      text: input.text,
      mediaKind: input.mediaKind,
      userLabel: input.userLabel,
      userAvatarSrc: storyAvatarSrcForStorage(input.userAvatarSrc),
      imageFile: input.imageFile,
      audioFile: input.audioFile,
      videoFile: input.videoFile,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'SocialPostLimitError') {
      throw new SocialPostLimitError();
    }
    throw err;
  }
}

export type StoryMediaUrls = {
  image: string | null;
  audio: string | null;
  video: string | null;
};

/** Resolved playback URLs for a story record (already joined at parse time). */
export function socialPostMediaUrls(post: SocialPostRecord): StoryMediaUrls {
  return {
    image: post.imageUrl?.trim() || null,
    audio: post.audioUrl?.trim() || null,
    video: post.videoUrl?.trim() || null,
  };
}

/** @deprecated Stories use API URLs — no object URLs to revoke. */
export function revokeSocialPostObjectUrl(_url: string | null | undefined): void {
  /* no-op */
}
